import { useEffect, useState } from 'react';
import { api } from '../lib/api';

interface Application {
  sk: string;
  company: string;
  role: string;
  type: string;
  status: string;
  deadline: string;
  _overdue_follow_up: boolean;
  _no_response_flag: boolean;
  _days_since_applied: number | null;
}

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700',
  applied: 'bg-blue-100 text-blue-700',
  acknowledged: 'bg-cyan-100 text-cyan-700',
  interview: 'bg-green-100 text-green-700',
  offer: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-red-100 text-red-700',
  withdrawn: 'bg-orange-100 text-orange-700',
  no_response: 'bg-yellow-100 text-yellow-700',
};

export default function TrackerPage() {
  const [applications, setApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');

  useEffect(() => { loadApplications(); }, []);

  async function loadApplications() {
    try {
      const params = filter ? `?status=${filter}` : '';
      const data = await api.get<{ items: Application[] }>(`/api/tracker${params}`);
      setApplications(data.items);
    } catch {}
    setLoading(false);
  }

  useEffect(() => { loadApplications(); }, [filter]);

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Application Tracker</h1>

      {/* Filters */}
      <div className="flex gap-2 mb-4 flex-wrap" role="group" aria-label="Status filter">
        {['', 'draft', 'applied', 'interview', 'offer', 'rejected'].map((status) => (
          <button
            key={status}
            onClick={() => setFilter(status)}
            className={`px-3 py-1 rounded-full text-sm border transition-colors ${
              filter === status
                ? 'bg-blue-600 text-white border-blue-600'
                : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
            }`}
          >
            {status || 'All'}
          </button>
        ))}
      </div>

      {loading && <p className="text-gray-500">Loading...</p>}

      {/* Application cards */}
      <div className="space-y-3">
        {applications.map((app) => (
          <article
            key={app.sk}
            className={`bg-white rounded-lg border p-4 ${
              app._overdue_follow_up ? 'border-red-300 bg-red-50' : 'border-gray-200'
            }`}
            aria-label={`${app.company} — ${app.role}`}
          >
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium text-gray-900">{app.company}</h3>
                <p className="text-sm text-gray-600">{app.role}</p>
              </div>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[app.status] ?? 'bg-gray-100'}`}>
                {app.status}
              </span>
            </div>

            <div className="mt-2 flex gap-4 text-xs text-gray-500">
              {app.deadline && <span>Deadline: {app.deadline}</span>}
              {app.type && <span className="capitalize">{app.type}</span>}
              {app._days_since_applied !== null && (
                <span>{app._days_since_applied} days since applied</span>
              )}
            </div>

            {app._overdue_follow_up && (
              <p className="mt-2 text-xs text-red-600 font-medium" role="alert">
                ⚠️ Follow-up is overdue
              </p>
            )}
            {app._no_response_flag && (
              <p className="mt-2 text-xs text-amber-600 font-medium" role="alert">
                ⏰ No response after 14+ days — consider following up
              </p>
            )}
          </article>
        ))}
      </div>

      {!loading && applications.length === 0 && (
        <p className="text-gray-500 text-center py-8">
          No applications yet. Use the Pipeline to create your first application.
        </p>
      )}
    </div>
  );
}
